import {
  ReactNode,
  type Dispatch,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuthSession, type AuthSession } from "@/contexts/useAuthSession";
import type {
  InterfaceBuilderComponentPropertyDraft,
} from "@/interfaceBuilder/componentPropertyDrafts";
import {
  readInterfaceBuilderInspectorOpen,
  writeInterfaceBuilderInspectorOpen,
} from "@/storage/interfaceBuilderPreferences";

export type InterfaceBuilderSelection =
  | {
      kind: "workspace";
      id: "workspace";
    }
  | {
      kind: "sidebar";
      id: "sidebar";
    }
  | {
      kind: "windows";
      id: "windows";
    }
  | {
      kind: "sidebarItem";
      id: string;
      componentId: string | null;
      sidebarItemId: string;
    }
  | {
      kind: "component";
      id: string;
      componentKind: string;
      componentPath: string | null;
      componentId: string;
    };

interface UserContextValue {
  session: AuthSession;
  isInterfaceBuilderMode: boolean;
  isInterfaceBuilderInspectorOpen: boolean;
  interfaceBuilderComponentPropertyDrafts: readonly InterfaceBuilderComponentPropertyDraft[];
  // The primary selection: the one the properties panel follows. It is the last
  // entry of `interfaceBuilderSelections`, which holds the whole Ctrl/Cmd-picked
  // set (a single plain click leaves exactly one entry in it).
  interfaceBuilderSelection: InterfaceBuilderSelection | null;
  interfaceBuilderSelections: readonly InterfaceBuilderSelection[];
  // When set, the workspace canvas renders only this component (and its
  // children) in isolation so it can be edited without surrounding chrome.
  interfaceBuilderFocusComponentId: string | null;
  setInterfaceBuilderSelection: (
    selection: InterfaceBuilderSelection | null,
  ) => void;
  // Replaces the whole set at once, the last entry becoming the primary
  // selection. Shift+click in the inspector tree selects a range this way:
  // one set, rather than a toggle per node in it.
  setInterfaceBuilderSelections: (
    selections: readonly InterfaceBuilderSelection[],
  ) => void;
  // Ctrl/Cmd+click: adds the selection to the set, or drops it when it is
  // already there.
  toggleInterfaceBuilderSelection: (
    selection: InterfaceBuilderSelection,
  ) => void;
  setInterfaceBuilderFocusComponentId: (componentId: string | null) => void;
  setInterfaceBuilderComponentPropertyDrafts: Dispatch<
    SetStateAction<readonly InterfaceBuilderComponentPropertyDraft[]>
  >;
  setInterfaceBuilderInspectorOpen: (open: boolean) => void;
  setInterfaceBuilderMode: (enabled: boolean) => void;
  toggleInterfaceBuilderInspector: () => void;
  toggleInterfaceBuilderMode: () => void;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

// Stable empty selection set, so an empty selection keeps a constant identity.
const EMPTY_SELECTIONS: readonly InterfaceBuilderSelection[] = [];

export function UserProvider({ children }: { children: ReactNode }) {
  const session = useAuthSession();
  const [isInterfaceBuilderMode, setInterfaceBuilderMode] = useState(false);
  const [
    isInterfaceBuilderInspectorOpen,
    setInterfaceBuilderInspectorOpenState,
  ] = useState(readInterfaceBuilderInspectorOpen);
  const [interfaceBuilderSelections, setSelections] = useState<
    readonly InterfaceBuilderSelection[]
  >(EMPTY_SELECTIONS);
  const interfaceBuilderSelection = interfaceBuilderSelections.length > 0
    ? interfaceBuilderSelections[interfaceBuilderSelections.length - 1]
    : null;
  const [
    interfaceBuilderFocusComponentId,
    setInterfaceBuilderFocusComponentId,
  ] = useState<string | null>(null);
  const [
    interfaceBuilderComponentPropertyDrafts,
    setInterfaceBuilderComponentPropertyDrafts,
  ] = useState<readonly InterfaceBuilderComponentPropertyDraft[]>([]);

  const setInterfaceBuilderSelection = useCallback((
    selection: InterfaceBuilderSelection | null,
  ) => {
    setSelections(selection ? [selection] : EMPTY_SELECTIONS);
  }, []);

  const setInterfaceBuilderSelections = useCallback((
    selections: readonly InterfaceBuilderSelection[],
  ) => {
    setSelections(selections.length > 0 ? [...selections] : EMPTY_SELECTIONS);
  }, []);

  const toggleInterfaceBuilderSelection = useCallback((
    selection: InterfaceBuilderSelection,
  ) => {
    setSelections((currentSelections) => {
      if (!currentSelections.some((entry) => entry.id === selection.id)) {
        return [...currentSelections, selection];
      }

      // Dropping the primary selection hands the properties panel to whichever
      // selection was picked before it.
      return currentSelections.filter((entry) => entry.id !== selection.id);
    });
  }, []);

  const setInterfaceBuilderInspectorOpen = useCallback((open: boolean) => {
    setInterfaceBuilderInspectorOpenState(open);
    writeInterfaceBuilderInspectorOpen(open);
  }, []);

  const toggleInterfaceBuilderMode = useCallback(() => {
    setInterfaceBuilderMode((enabled) => !enabled);
  }, []);

  const toggleInterfaceBuilderInspector = useCallback(() => {
    setInterfaceBuilderInspectorOpenState((open) => {
      const nextOpen = !open;
      writeInterfaceBuilderInspectorOpen(nextOpen);
      return nextOpen;
    });
  }, []);

  useEffect(() => {
    if (session.isAuthenticated) return;

    setInterfaceBuilderMode(false);
    setInterfaceBuilderInspectorOpenState(false);
    setSelections(EMPTY_SELECTIONS);
    setInterfaceBuilderFocusComponentId(null);
    setInterfaceBuilderComponentPropertyDrafts([]);
  }, [session.isAuthenticated]);

  useEffect(() => {
    if (isInterfaceBuilderMode) {
      setInterfaceBuilderInspectorOpenState(readInterfaceBuilderInspectorOpen());
      return;
    }

    setInterfaceBuilderInspectorOpenState(false);
    setSelections(EMPTY_SELECTIONS);
    setInterfaceBuilderFocusComponentId(null);
    setInterfaceBuilderComponentPropertyDrafts([]);
  }, [isInterfaceBuilderMode]);

  const value = useMemo<UserContextValue>(
    () => ({
      session,
      isInterfaceBuilderMode,
      isInterfaceBuilderInspectorOpen,
      interfaceBuilderComponentPropertyDrafts,
      interfaceBuilderSelection,
      interfaceBuilderSelections,
      interfaceBuilderFocusComponentId,
      setInterfaceBuilderSelection,
      setInterfaceBuilderSelections,
      toggleInterfaceBuilderSelection,
      setInterfaceBuilderFocusComponentId,
      setInterfaceBuilderComponentPropertyDrafts,
      setInterfaceBuilderInspectorOpen,
      setInterfaceBuilderMode,
      toggleInterfaceBuilderInspector,
      toggleInterfaceBuilderMode,
    }),
    [
      session,
      isInterfaceBuilderMode,
      isInterfaceBuilderInspectorOpen,
      interfaceBuilderComponentPropertyDrafts,
      interfaceBuilderSelection,
      interfaceBuilderSelections,
      interfaceBuilderFocusComponentId,
      setInterfaceBuilderSelection,
      setInterfaceBuilderSelections,
      toggleInterfaceBuilderSelection,
      toggleInterfaceBuilderInspector,
      toggleInterfaceBuilderMode,
    ],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser(): UserContextValue {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return ctx;
}
